<template>
  <div class="hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
    <!-- Sticky while this section's body scrolls underneath, so it's always
         clear which section is open — stickyTop is this page's own measured
         header height, passed down from factsheet.vue. -->
    <div class="px-5 py-4 flex items-center justify-between sticky z-[5] bg-white dark:bg-[#1C2128]" :style="{ top: `${stickyTop}px` }">
      <div class="flex items-center gap-2">
        <button type="button" title="Collapse section" @click="toggleCollapsed"
          class="w-5 h-5 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer flex-shrink-0">
          <svg class="w-3.5 h-3.5 transition-transform" :class="collapsed ? '-rotate-90' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
          </svg>
        </button>
        <div class="w-6 h-6 rounded-md bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
          <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"/>
          </svg>
        </div>
        <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Companies &amp; Departments</h2>
      </div>
      <button v-if="canEdit" type="button" title="Edit" @click="toggleEditingAttachments"
        :class="editingAttachments ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'"
        class="w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
        </svg>
      </button>
    </div>

    <div v-show="!collapsed" class="px-5 py-5">
      <!-- Empty state: no companies exist in this organization at all -->
      <div v-if="structureStore.fetchedAt && structureStore.companies.length === 0"
        class="flex flex-col items-center text-center py-6">
        <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
          <svg class="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"/>
          </svg>
        </div>
        <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">No company added yet</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 mb-4 max-w-xs">Add a company (and its departments) before attaching applications to it.</p>
        <button v-if="canEdit" type="button" @click="openAddCompany"
          class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm mb-3">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
          </svg>
          Add Company
        </button>
        <router-link :to="`/organizations/${organizationUrl}/structure`" data-turbo-prefetch="false"
          class="inline-flex items-center gap-1 text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
          or go to Structure to manage companies
        </router-link>
      </div>

      <template v-else>
        <!-- Currently attached -->
        <p v-if="attachedByCompany.length === 0" class="text-sm text-gray-400 dark:text-gray-500 mb-5">
          Not used by any company or department yet.
        </p>
        <div v-else class="space-y-3 mb-5">
          <div v-for="group in attachedByCompany" :key="group.companyId">
            <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">{{ group.companyName }}</p>
            <div class="flex flex-wrap gap-1.5">
              <span v-for="dept in group.departments" :key="dept.id"
                class="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400"
                :class="{ 'pr-2.5': !editingAttachments }">
                {{ dept.name }}
                <button v-if="editingAttachments" type="button" title="Remove" @click="detachDepartment(dept.id)"
                  class="w-4 h-4 flex items-center justify-center rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                  <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </span>
            </div>
          </div>
        </div>

        <!-- Add flow -->
        <div v-if="editingAttachments" class="border-t border-gray-100 dark:border-gray-700 pt-4">
          <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Attach to a company</p>
          <div class="flex flex-col sm:flex-row gap-2">
            <select v-model="selectedCompanyId"
              class="sm:w-56 px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="">Select a company</option>
              <option v-for="company in structureStore.companies" :key="company.id" :value="company.id">{{ company.name }}</option>
            </select>

            <div class="relative dept-picker flex-1">
              <button type="button" :disabled="!selectedCompanyId" @click="deptDropdownOpen = !deptDropdownOpen"
                class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-left disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500">
                <span v-if="!selectedCompanyId" class="text-gray-400 dark:text-gray-500">Select a company first</span>
                <span v-else-if="selectedDepartmentIds.length === 0" class="text-gray-400 dark:text-gray-500">Select departments</span>
                <span v-else class="text-gray-900 dark:text-gray-100">{{ selectedDepartmentIds.length }} department{{ selectedDepartmentIds.length === 1 ? '' : 's' }} selected</span>
              </button>
              <div v-if="deptDropdownOpen && selectedCompanyId"
                class="absolute z-20 mt-1.5 w-full bg-white dark:bg-[#1C2128] border border-gray-100 dark:border-gray-700 rounded-lg shadow-lg py-1.5 max-h-56 overflow-y-auto">
                <label v-if="availableDepartments.length" class="flex items-center gap-2.5 px-3 py-1.5 border-b border-gray-100 dark:border-gray-700 cursor-pointer select-none font-semibold text-gray-700 dark:text-gray-300">
                  <input type="checkbox" :checked="allDepartmentsSelected" @change="toggleSelectAllDepartments"
                    class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0" />
                  Select all
                </label>
                <label v-for="dept in availableDepartments" :key="dept.id"
                  class="flex items-center gap-2.5 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer select-none">
                  <input type="checkbox" :value="dept.id" v-model="selectedDepartmentIds"
                    class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0" />
                  {{ dept.name }}
                </label>
                <p v-if="availableDepartments.length === 0" class="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">
                  All departments of this company are already attached.
                </p>
              </div>
            </div>

            <button type="button" @click="saveDepartmentAttachments"
              :disabled="selectedDepartmentIds.length === 0 || attachingDepartments"
              class="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex-shrink-0">
              Save
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>

  <!-- Add company modal (empty state of this section) -->
  <AddNodeModal v-if="addCompanyModal.open"
    title="Add Company"
    type="company"
    submit-label="Add"
    :saving="addCompanyModal.saving"
    :error="addCompanyModal.error"
    @close="addCompanyModal.open = false"
    @submit="submitAddCompany" />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useOrgApplicationsStore } from 'stores/orgApplications.js'
import { useCompanyStructureStore } from 'stores/companyStructure.js'
import { useApplicationFactsheetSections } from 'composables/useApplicationFactsheetSections.js'
import AddNodeModal from 'controllers/vue_components/organizations/structure/AddNodeModal.vue'

const props = defineProps({
  application: { type: Object, required: true },
  organizationId: { type: String, required: true },
  organizationUrl: { type: String, required: true },
  canEdit: { type: Boolean, default: false },
  stickyTop: { type: Number, default: 0 },
})

const emit = defineEmits(['updated', 'notify'])

const store = useOrgApplicationsStore()
const structureStore = useCompanyStructureStore()
const sectionPrefs = useApplicationFactsheetSections()
const SECTION_KEY = 'companies_departments'

const collapsed = computed(() => sectionPrefs.isCollapsed(SECTION_KEY))

function toggleCollapsed() {
  sectionPrefs.toggleSection(SECTION_KEY)
}

const editingAttachments = ref(false)
const selectedCompanyId = ref('')
const selectedDepartmentIds = ref([])
const deptDropdownOpen = ref(false)
const attachingDepartments = ref(false)
const addCompanyModal = ref({ open: false, saving: false, error: '' })

function toggleEditingAttachments() {
  editingAttachments.value = !editingAttachments.value
  selectedCompanyId.value = ''
  selectedDepartmentIds.value = []
  deptDropdownOpen.value = false
  if (editingAttachments.value) sectionPrefs.expandSection(SECTION_KEY)
}

function actionErrorMessage(res) {
  const validationErrors = res.error?.data?.data
  if (Array.isArray(validationErrors) && validationErrors.length) return validationErrors.join(', ')
  return 'Something went wrong. Please try again.'
}

const attachedByCompany = computed(() => {
  if (!props.application.departments?.length) return []
  const groups = {}
  props.application.departments.forEach((dept) => {
    if (!groups[dept.company_id]) groups[dept.company_id] = { companyId: dept.company_id, companyName: dept.company_name, departments: [] }
    groups[dept.company_id].departments.push(dept)
  })
  return Object.values(groups)
})

const availableDepartments = computed(() => {
  const company = structureStore.companies.find((c) => c.id === selectedCompanyId.value)
  if (!company) return []
  const attachedIds = new Set((props.application.departments || []).map((d) => d.id))
  return company.departments.filter((d) => !attachedIds.has(d.id))
})

const allDepartmentsSelected = computed(() =>
  availableDepartments.value.length > 0 && selectedDepartmentIds.value.length === availableDepartments.value.length
)

function toggleSelectAllDepartments() {
  selectedDepartmentIds.value = allDepartmentsSelected.value ? [] : availableDepartments.value.map((d) => d.id)
}

watch(selectedCompanyId, () => {
  selectedDepartmentIds.value = []
})

function handleDeptDropdownOutsideClick(e) {
  if (!e.target.closest('.dept-picker')) deptDropdownOpen.value = false
}

async function saveDepartmentAttachments() {
  if (selectedDepartmentIds.value.length === 0) return

  attachingDepartments.value = true
  const res = await store.attachDepartments(props.application.id, selectedDepartmentIds.value)
  attachingDepartments.value = false

  if (!res.js_error && res.response.success) {
    emit('updated', { ...props.application, departments: res.response.data.departments })
    selectedCompanyId.value = ''
    selectedDepartmentIds.value = []
    deptDropdownOpen.value = false
  } else {
    emit('notify', actionErrorMessage(res), 'alert')
  }
}

async function detachDepartment(departmentId) {
  const res = await store.detachDepartment(props.application.id, departmentId)

  if (!res.js_error && res.response.success) {
    emit('updated', { ...props.application, departments: res.response.data.departments })
  } else {
    emit('notify', actionErrorMessage(res), 'alert')
  }
}

function openAddCompany() {
  addCompanyModal.value = { open: true, saving: false, error: '' }
}

// Mirrors structure/index.vue's submitModal for type: 'company' — same store
// actions, same "prefill with departments" flow from AddNodeModal.
async function submitAddCompany(payload) {
  addCompanyModal.value.saving = true
  addCompanyModal.value.error = ''

  const { departments: prefillDepartmentNames, ...corePayload } = payload
  const res = await structureStore.addCompany(corePayload)

  if (!res.js_error && res.response.success && prefillDepartmentNames?.length) {
    const companyId = res.response.data.id
    await Promise.all(prefillDepartmentNames.map((deptName) => structureStore.addDepartment(companyId, { name: deptName })))
  }

  addCompanyModal.value.saving = false

  if (!res.js_error && res.response.success) {
    addCompanyModal.value.open = false
    emit('notify', 'Company created successfully.', 'notice')
  } else {
    addCompanyModal.value.error = actionErrorMessage(res)
  }
}

onMounted(() => {
  document.addEventListener('click', handleDeptDropdownOutsideClick)
  structureStore.fetchIfStale(props.organizationId)
})

onUnmounted(() => {
  document.removeEventListener('click', handleDeptDropdownOutsideClick)
})
</script>
